import { Head, router } from "@inertiajs/react"
import { ClipboardList, ChevronRight } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const submissionsTabs = [
  { label: "Form Submissions", href: "/app/admin/submissions" },
  { label: "Feedback Requests", href: "/app/admin/feedback_submissions" },
]
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const TYPE_CONFIG = {
  lead: { label: "Lead", className: "bg-red-100 text-red-800" },
  story_application: { label: "Story App", className: "bg-amber-100 text-amber-800" },
}

const STATUS_CONFIG = {
  // Lead statuses
  matched: { label: "Matched", className: "bg-green-100 text-green-800" },
  no_match: { label: "No Match", className: "bg-red-100 text-red-800" },
  // Application statuses
  pending: { label: "Pending", className: "bg-zinc-100 text-zinc-700" },
  reviewed: { label: "Reviewed", className: "bg-blue-100 text-blue-800" },
  contacted: { label: "Contacted", className: "bg-purple-100 text-purple-800" },
  completed: { label: "Completed", className: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-800" },
}

function buildUrl(params) {
  const query = Object.entries(params)
    .filter(([, v]) => v && v !== "all")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&")
  return `/app/admin/submissions${query ? `?${query}` : ""}`
}

export default function SubmissionsIndex({
  currentUser,
  submissions,
  pendingCount,
  submissionsPendingCount,
  currentType,
  currentStatus,
  pagination,
}) {
  const handleTypeFilter = (value) => {
    router.get(
      buildUrl({ type: value === "all" ? null : value, status: currentStatus }),
      {},
      { preserveState: true }
    )
  }

  const handleStatusFilter = (value) => {
    router.get(
      buildUrl({ type: currentType, status: value === "all" ? null : value }),
      {},
      { preserveState: true }
    )
  }

  return (
    <AdminLayout
      currentUser={currentUser}
      pendingCount={pendingCount}
      applicationsPendingCount={submissionsPendingCount}
    >
      <Head title="Submissions — Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={submissionsTabs} />

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Submissions</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {pagination?.totalCount ?? 0} submission{pagination?.totalCount !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={currentType || "all"} onValueChange={handleTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="lead">Leads</SelectItem>
                <SelectItem value="story_application">Story Apps</SelectItem>
              </SelectContent>
            </Select>
            <Select value={currentStatus || "all"} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {currentType !== "story_application" && (
                  <>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="no_match">No Match</SelectItem>
                  </>
                )}
                {currentType !== "lead" && (
                  <>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {submissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
                <ClipboardList size={36} className="mb-3 opacity-40" />
                <p className="text-sm font-medium">No submissions yet</p>
                <p className="text-xs mt-1">Form submissions will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Details</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell w-[100px]">Date</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((sub) => {
                    const typeConfig = TYPE_CONFIG[sub.type] || TYPE_CONFIG.lead
                    const statusConfig = STATUS_CONFIG[sub.status] || { label: sub.statusLabel, className: "bg-zinc-100 text-zinc-700" }

                    return (
                      <TableRow
                        key={`${sub.type}-${sub.id}`}
                        className="cursor-pointer hover:bg-zinc-50"
                        onClick={() => router.visit(sub.detailUrl)}
                      >
                        <TableCell>
                          <Badge className={typeConfig.className}>{typeConfig.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium text-zinc-800">{sub.name}</p>
                          <p className="text-xs text-zinc-400">{sub.email}</p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <p className="text-sm text-zinc-700">{sub.company}</p>
                          {sub.type === "lead" && sub.assignedCoach && (
                            <p className="text-xs text-zinc-400">&rarr; Coach: {sub.assignedCoach}</p>
                          )}
                          {sub.type === "lead" && !sub.assignedCoach && (
                            <p className="text-xs text-red-400">&rarr; Admin Fallback</p>
                          )}
                          {sub.type === "story_application" && sub.location && (
                            <p className="text-xs text-zinc-400">{sub.location}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-xs text-zinc-400 whitespace-nowrap">
                          {new Date(sub.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </TableCell>
                        <TableCell>
                          <ChevronRight size={14} className="text-zinc-300" />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              {pagination.currentPage > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href={buildUrl({
                      type: currentType,
                      status: currentStatus,
                      page: pagination.currentPage - 1,
                    })}
                  />
                </PaginationItem>
              )}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href={buildUrl({ type: currentType, status: currentStatus, page })}
                    isActive={page === pagination.currentPage}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {pagination.currentPage < pagination.totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href={buildUrl({
                      type: currentType,
                      status: currentStatus,
                      page: pagination.currentPage + 1,
                    })}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </AdminLayout>
  )
}
